import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SeoMetadata from "@/components/common/SeoMetadata";
import { useEffect } from "react";
import { saveSeoMetadata } from "@/utils/seoUtils";
import { Link } from "react-router-dom";
import { Caravan, ArrowRight, Shield, Navigation, Tent, Gauge, Clock, CheckCircle, FileText } from "lucide-react";
import { LainaCta } from "@/components/common/LainaCta";
import Image from "@/components/ui/image";
import { useIsMobile } from "@/hooks/use-mobile";
import CaravanTravelTips from "@/components/caravan/CaravanTravelTips";
import AffiliateAds from "@/components/common/AffiliateAds";
import { AspectRatio } from "@/components/ui/aspect-ratio";

const seoKeywords = [
  "caravanrahoitus", "matkailuautorahoitus", "asuntovaunu rahoitus",
  "matkailuauton osamaksu", "asuntoauton rahoitus", "matkailuvaunu laina",
  "edullinen asuntoauto", "caravan lainat", "matkailuauton hankinta",
  "camping suomi", "leirintäalueet suomessa", "matkailuauton huolto",
  "caravan lifestyle", "asuntovaunu vertailu", "matkailuauton varusteet"
];

const CaravanrahoitusPage = () => {
  const seoTitle = "Caravanrahoitus | Matkailuautojen ja -vaunujen rahoitus | Lainasampo";
  const seoDescription = "Kilpailuta edullinen rahoitus matkailuautoon tai -vaunuun. Vertaile lainatarjouksia ja säästä rahoituksessa. Kattava opas caravan-elämään ja matkailuauton hankintaan.";
  const pageUrl = "https://lainasampo.fi/lainat/caravanrahoitus";
  const isMobile = useIsMobile();

  useEffect(() => {
    saveSeoMetadata("/lainat/caravanrahoitus", {
      title: seoTitle,
      description: seoDescription,
      keywords: seoKeywords,
    });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SeoMetadata
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={pageUrl}
      />

      <div className="relative h-[60vh] md:h-[70vh] min-h-[300px] md:min-h-[400px] max-h-[500px] md:max-h-[600px]">
        <Image
          src="/lovable-uploads/632c70c3-da29-4b83-971f-24c4bc194bf8.png"
          alt="Matkailuauto talvisessa maisemassa järven rannalla"
          className="absolute inset-0 w-full h-full object-cover"
          fallbackSrc="/placeholder.svg"
          priority={true}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent flex items-center">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-2xl text-white space-y-4 md:space-y-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight drop-shadow-lg">
                Toteuta <span className="text-blue-400">unelmiesi caravan-elämä</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl drop-shadow-md">
                Kilpailuta matkailuauton tai -vaunun rahoitus ilmaiseksi. Vertaile tarjouksia ja löydä paras ratkaisu.
              </p>
              <div className="flex flex-col gap-4 relative">
                <div className="flex flex-col md:flex-row gap-3">
                  <LainaCta
                    text={isMobile ? "Hae lainaa" : "Hae caravanrahoitusta"}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold shadow-lg transform hover:scale-105 transition duration-300 w-full md:w-auto"
                    variant="primary"
                    size={isMobile ? "md" : "lg"}
                  />
                  {!isMobile && (
                    <Link to="/artikkelit/caravan-matkailu-suomessa-2025" className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 border border-white/30 backdrop-blur-sm text-white font-medium transition-all duration-300">
                      Lue caravan-opas <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  )}
                </div>

                <div className="w-full bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { text: "Päätös 24h", icon: <Clock className="h-4 w-4 text-blue-500" /> },
                      { text: "Korot alk. 4,9%", icon: <CheckCircle className="h-4 w-4 text-blue-500" /> },
                      { text: "5 000€ - 150 000€", icon: <Shield className="h-4 w-4 text-blue-500" /> },
                      { text: "1-10v maksuaika", icon: <Gauge className="h-4 w-4 text-blue-500" /> }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-gray-800">
                        {item.icon}
                        <span className="font-medium text-sm">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6">
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 -mt-4 md:mt-0 mb-8 md:mb-16">
          <div className="space-y-6">
            <div className="relative rounded-xl overflow-hidden">
              <div className="aspect-[4/3]">
                <Image
                  src="/lovable-uploads/e9993532-3704-4e85-96f5-45e165d33772.png"
                  alt="Pariskunta valmistamassa ruokaa matkailuautossa"
                  className="w-full h-full object-cover"
                  fallbackSrc="/placeholder.svg"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-8">
                <div className="absolute bottom-8 left-8 right-8">
                  <h3 className="text-white text-2xl font-bold mb-4">Ruokailu tien päällä</h3>
                  <ul className="mt-4 space-y-3">
                    {[
                      "Suunnittele ateriat etukäteen",
                      "Pakkaa mukaan monipuoliset mausteet",
                      "Hyödynnä paikalliset markkinat",
                      "Kokeile retkikeittimen reseptejä"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center text-white text-base">
                        <CheckCircle className="h-5 w-5 mr-3 text-blue-400" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="relative rounded-xl overflow-hidden group">
              <div className="aspect-[4/3]">
                <Image
                  src="/lovable-uploads/02ccf578-9f7a-45a6-8d7f-02971015e056.png"
                  alt="Perhe viettämässä aikaa matkailuauton luona"
                  className="w-full h-full object-cover"
                  fallbackSrc="/placeholder.svg"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-8">
                <div className="absolute bottom-8 left-8 right-8">
                  <h3 className="text-white text-2xl font-bold mb-4">Yhteisöllisyys ja seikkailu</h3>
                  <ul className="mt-4 space-y-3">
                    {[
                      "Jaa kokemuksia ystävien kanssa",
                      "Luo unohtumattomia muistoja",
                      "Tutustu uusiin paikkoihin yhdessä",
                      "Rakenna elinikäisiä ystävyyksiä"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center text-white text-base">
                        <CheckCircle className="h-5 w-5 mr-3 text-blue-400" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <CaravanTravelTips />

        <section>
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
              Caravan-elämän parhaat puolet
            </span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              {
                url: "/lovable-uploads/d3698304-fbbe-4b34-9402-4811f00d782b.png",
                title: "Vapaus ja joustavuus",
                description: "Suunnittele matkasi oman aikataulusi mukaan"
              },
              {
                url: "/lovable-uploads/6b48ede2-c623-4f31-8efa-83f95aa56925.png",
                title: "Luonnonläheisyys",
                description: "Nauti ainutlaatuisista maisemista ja hiljaisuudesta"
              },
              {
                url: "/lovable-uploads/0f16fa8b-6102-4802-983a-ddf3ca7ecea6.png",
                title: "Kustannustehokkuus",
                description: "Säästä majoitus- ja ruokakustannuksissa"
              }
            ].map((item, i) => (
              <div key={i} className="relative overflow-hidden rounded-xl group">
                <AspectRatio ratio={4/3} className="bg-gray-100">
                  <Image
                    src={item.url}
                    alt={item.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    fallbackSrc="/placeholder.svg"
                    priority={true}
                  />
                </AspectRatio>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent p-6">
                  <h3 className="text-white text-lg md:text-xl font-bold">{item.title}</h3>
                  <p className="text-white/90 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mt-4">
            {[
              {
                url: "/lovable-uploads/ae9feb3c-09e6-4096-befc-9aae1d5e0eb2.png",
                title: "Rauhallinen ilta",
                desc: "Nauti tunnelmallisista hetkistä"
              },
              {
                url: "/lovable-uploads/2aab45af-304e-4756-b1ff-eb0b0932797f.png",
                title: "Retkeily",
                desc: "Kokeile uusia seikkailuja"
              },
              {
                url: "/lovable-uploads/ec9a906d-c848-4008-9b85-b8bf37a1cb07.png",
                title: "Rantaelämää",
                desc: "Parhaat rantapaikat"
              },
              {
                url: "/lovable-uploads/02ccf578-9f7a-45a6-8d7f-02971015e056.png",
                title: "Yhteisöllisyys",
                desc: "Jaa kokemuksia ystävien kanssa"
              }
            ].map((item, i) => (
              <div key={i} className="relative overflow-hidden rounded-lg aspect-square group">
                <Image
                  src={item.url}
                  alt={item.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                  fallbackSrc="/placeholder.svg"
                  priority={i < 2}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-100 group-hover:opacity-90 transition-opacity duration-300">
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <h4 className="font-bold text-base">{item.title}</h4>
                    <p className="text-xs text-white/90">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <AffiliateAds />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          <div className="lg:col-span-2">
            <section className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl md:rounded-2xl overflow-hidden mb-8 md:mb-16 relative">
              <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
                <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                  <path fill="white" d="M47.1,-57.8C58.5,-45.7,63.7,-28.7,66.8,-11.1C70,6.5,71.1,24.5,63.5,38.6C55.9,52.7,39.6,62.8,22.2,67.6C4.9,72.3,-13.5,71.6,-29.6,64.7C-45.8,57.8,-59.6,44.7,-68.5,28C-77.4,11.3,-81.3,-9,-74.5,-25.3C-67.8,-41.6,-50.5,-54,-33.7,-65.1C-16.9,-76.2,-0.6,-86,14.8,-83.5C30.3,-81,35.7,-69.9,47.1,-57.8Z" />
                </svg>
              </div>
              <div className="relative p-5 md:p-8 text-white">
                <h2 className="text-2xl md:text-3xl font-bold mb-2 md:mb-4">Aloita caravan-seikkailusi tänään!</h2>
                <p className="text-sm md:text-lg mb-4 md:mb-6 max-w-2xl">
                  Kilpailuta lainatarjoukset ilmaiseksi ja ilman sitoumuksia. Saat lainapäätöksen nopeasti ja voit valita
                  juuri sinulle sopivan rahoitusvaihtoehdon.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                  <LainaCta
                    text={isMobile ? "Hae rahoitusta" : "Hae caravanrahoitusta"}
                    className="bg-white text-blue-700 hover:bg-blue-50 font-bold"
                    variant="primary"
                    size={isMobile ? "md" : "lg"}
                  />
                  {!isMobile && (
                    <Link to="/lainatuotteet" className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-blue-700 hover:bg-blue-800 border border-blue-500 text-white font-medium transition-all duration-300">
                      Vertaile lainatuotteita <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  )}
                </div>

                <div className="mt-6 bg-white/20 backdrop-blur-sm rounded-xl p-4 flex flex-wrap gap-3">
                  {[
                    { text: "Katso myös nämä rahoitusvaihtoehdot:", className: "w-full font-bold text-white mb-1" },
                    { text: "Autolaina", url: "/lainat/autolaina", className: "bg-white/20 hover:bg-white/30" },
                    { text: "Venelaina", url: "/lainat/venelaina", className: "bg-white/20 hover:bg-white/30" },
                    { text: "Moottoripyörälaina", url: "/lainat/moottoripyoralaina", className: "bg-white/20 hover:bg-white/30" },
                    { text: "Kulutusluotto", url: "/lainat/kulutusluotto", className: "bg-white/20 hover:bg-white/30" }
                  ].map((item, i) => (
                    item.url ? (
                      <Link
                        key={i}
                        to={item.url}
                        className={`${item.className} px-3 py-2 rounded-lg text-white text-sm font-medium transition-colors flex items-center`}
                      >
                        <span>{item.text}</span>
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    ) : (
                      <div key={i} className={item.className}>{item.text}</div>
                    )
                  ))}
                </div>

                <div className="mt-4">
                  <Link 
                    to="/artikkelit/caravan-matkailu-suomessa-2025"
                    className="inline-flex items-center text-white hover:text-blue-100 font-medium transition-colors"
                  >
                    <span>Caravan-matkailu Suomessa 2025 - Lue artikkeli</span>
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            </section>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="relative overflow-hidden rounded-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800"></div>
                <div className="absolute top-0 left-0 w-full h-full opacity-10">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path d="M0,0 L100,0 L100,100 L0,100 Z" fill="url(#pattern)" />
                    <defs>
                      <pattern id="pattern" patternUnits="userSpaceOnUse" width="10" height="10">
                        <circle cx="5" cy="5" r="1" fill="white" />
                      </pattern>
                    </defs>
                  </svg>
                </div>
                <div className="relative p-6 text-white">
                  <h2 className="text-2xl font-bold mb-4">Kilpailuta caravanrahoitus nyt!</h2>
                  <p className="mb-6">
                    Matkailuajoneuvon rahoituksessa voit säästää jopa 30% kokonaiskustannuksissa kilpailuttamalla!
                  </p>
                  <LainaCta
                    variant="primary"
                    size="lg"
                    text="Hae caravanrahoitusta"
                    className="w-full bg-white text-blue-700 hover:bg-blue-50 font-bold shadow-md"
                  />
                </div>
              </div>

              <Card className="border-blue-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
                  <CardTitle className="text-lg text-white">Caravanrahoitus lyhyesti</CardTitle>
                </div>
                <CardContent className="pt-5">
                  <div className="space-y-3">
                    {[
                      { label: "Laina-aika:", value: "1-10 vuotta" },
                      { label: "Lainasumma:", value: "5 000€ - 150 000€" },
                      { label: "Korko alkaen:", value: "4,9%" },
                      { label: "Vakuus:", value: "Vakuudeton tai ajoneuvo vakuutena" },
                      { label: "Käsittelyaika:", value: "1-3 arkipäivää" }
                    ].map((item, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-gray-100 last:border-0">
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-bold text-blue-800">{item.value}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <span className="font-bold">Säästövinkki:</span> Kilpailuttamalla voit säästää jopa 30% rahoituksen kokonaiskustannuksissa!
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="font-semibold text-lg mb-4 text-gray-800">Euroopan parhaat camping-kohteet</h3>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { 
                      country: "Norja", 
                      highlight: "Vuonoreitit ja Geirangervuono",
                      url: "https://www.camping.no" 
                    },
                    { 
                      country: "Ruotsi", 
                      highlight: "Tukholman saaristo", 
                      url: "https://www.camping.se" 
                    },
                    { 
                      country: "Saksa", 
                      highlight: "Romantische Straße", 
                      url: "https://www.camping.info/de" 
                    }
                  ].map((dest, i) => (
                    <a
                      key={i}
                      href={dest.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col p-3 rounded-lg bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-100 text-gray-800 hover:text-blue-700 transition-colors group"
                    >
                      <span className="font-bold">{dest.country}</span>
                      <span className="text-sm text-gray-500">{dest.highlight}</span>
                      <span className="text-xs text-blue-600 mt-1 group-hover:underline flex items-center">
                        Tutustu leirintäalueisiin
                        <ArrowRight className="ml-1 h-3 w-3" />
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaravanrahoitusPage;
